import { FormEvent, KeyboardEvent, useMemo, useState } from "react";
import { ToastFeedback } from "Components/atoms/ToastFeedback";
import { updateUserProfile } from "shared/auth/auth";
import { useAuth } from "shared/contexts/AuthContext";
import { sendGTMEvent } from "shared/services/GTM";
import { getPlatformType } from "shared/utils/platform";
import {
  ErrorText,
  Field,
  Form,
  Hint,
  Input,
  Label,
  SaveButton,
} from "./styles";

const NAME_PATTERN = /^[\p{L}]+(?:[ '‐-][\p{L}]+)*$/u;
function getNationalPhone(value: string) {
  const digits = value.replace(/\D/g, "");

  if (value.trim().startsWith("+55")) {
    return digits.slice(2, 13);
  }

  return digits.startsWith("55") && digits.length > 11
    ? digits.slice(2, 13)
    : digits.slice(0, 11);
}

function formatPhone(value: string) {
  const digits = getNationalPhone(value);
  if (!digits) return "";

  const areaCode = digits.slice(0, 2);
  const firstPart = digits.slice(2, digits.length === 10 ? 6 : 7);
  const lastPart = digits.slice(digits.length === 10 ? 6 : 7, 11);

  let formatted = "+55";
  if (areaCode) formatted += ` (${areaCode}`;
  if (areaCode.length === 2) formatted += ")";
  if (firstPart) formatted += ` ${firstPart}`;
  if (lastPart) formatted += `-${lastPart}`;
  return formatted;
}

export function PersonalDataForm() {
  const { user, refreshSession } = useAuth();
  const initialName = user?.name ?? "";
  const initialEmail = user?.email ?? "";
  const initialPhone = formatPhone(user?.whatsappNumber ?? "");
  const [name, setName] = useState(initialName);
  const [phone, setPhone] = useState(initialPhone);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const errors = useMemo(() => {
    const normalizedName = name.trim();
    const phoneDigits = getNationalPhone(phone);

    return {
      name:
        !normalizedName && !initialName
          ? ""
          : normalizedName.length < 2
            ? "Informe pelo menos 2 caracteres."
            : !NAME_PATTERN.test(normalizedName)
              ? "Use apenas letras, espaços, hífen ou apóstrofo."
              : "",
      phone:
        (!phoneDigits.length && !initialPhone) ||
        phoneDigits.length === 10 ||
        phoneDigits.length === 11
          ? ""
          : "Informe um telefone com DDD.",
    };
  }, [initialName, initialPhone, name, phone]);

  const hasChanges =
    name.trim() !== initialName.trim() || phone !== initialPhone;
  const hasErrors = Object.values(errors).some(Boolean);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!hasChanges || hasErrors || submitting) return;

    setSubmitting(true);
    setFeedback(null);
    const fieldsChanged: Array<"name" | "whatsapp"> = [];

    if (name.trim() !== initialName.trim()) fieldsChanged.push("name");
    if (phone !== initialPhone) fieldsChanged.push("whatsapp");

    try {
      await updateUserProfile({
        name: name.trim(),
        whatsappNumber: phone ? `+55${getNationalPhone(phone)}` : "",
      });
      await refreshSession();
      sendGTMEvent("accountProfileUpdate", {
        platform: getPlatformType(),
        result: "success",
        fieldsChanged,
      });

      setFeedback({
        type: "success",
        message: "Alterações salvas com sucesso.",
      });
    } catch (error) {
      sendGTMEvent("accountProfileUpdate", {
        platform: getPlatformType(),
        result: "error",
        fieldsChanged,
      });
      setFeedback({
        type: "error",
        message:
          error instanceof Error
            ? error.message
            : "Não foi possível salvar as alterações.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePhoneKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Backspace") return;

    const cursorPosition = event.currentTarget.selectionStart;
    if (!cursorPosition || /\d/.test(phone[cursorPosition - 1] ?? "")) return;

    let digitIndex = cursorPosition - 2;
    while (digitIndex >= 0 && !/\d/.test(phone[digitIndex])) {
      digitIndex -= 1;
    }

    // Não remove os dígitos fixos do código do país; sem número nacional,
    // o campo deve simplesmente ficar vazio.
    if (digitIndex <= 2) {
      event.preventDefault();
      setPhone("");
      return;
    }

    event.preventDefault();
    const nextValue = `${phone.slice(0, digitIndex)}${phone.slice(digitIndex + 1)}`;
    setPhone(formatPhone(nextValue));
  };

  return (
    <Form onSubmit={handleSubmit} noValidate>
      <ToastFeedback
        message={feedback?.message ?? ""}
        type={feedback?.type ?? "success"}
        isOpen={Boolean(feedback)}
        onClose={() => setFeedback(null)}
      />

      <Field>
        <Label htmlFor="account-name">Nome Completo</Label>
        <Input
          id="account-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          autoComplete="name"
          $hasError={Boolean(errors.name)}
          aria-invalid={Boolean(errors.name)}
          aria-describedby={errors.name ? "account-name-error" : undefined}
        />
        {errors.name && (
          <ErrorText id="account-name-error">{errors.name}</ErrorText>
        )}
      </Field>

      <Field>
        <Label htmlFor="account-email">E-mail</Label>
        <Input
          id="account-email"
          type="email"
          value={initialEmail}
          autoComplete="email"
          disabled
          aria-describedby="account-email-hint"
        />
        <Hint id="account-email-hint">
          A alteração do e-mail não está disponível no momento.
        </Hint>
      </Field>

      <Field>
        <Label htmlFor="account-phone">WhatsApp</Label>
        <Input
          id="account-phone"
          type="tel"
          value={phone}
          onChange={(event) => setPhone(formatPhone(event.target.value))}
          onKeyDown={handlePhoneKeyDown}
          autoComplete="tel"
          inputMode="tel"
          placeholder="+55 (00) 00000-0000"
          maxLength={19}
          $hasError={Boolean(errors.phone)}
          aria-invalid={Boolean(errors.phone)}
          aria-describedby={errors.phone ? "account-phone-error" : undefined}
        />
        {errors.phone && (
          <ErrorText id="account-phone-error">{errors.phone}</ErrorText>
        )}
      </Field>

      <SaveButton
        type="submit"
        disabled={!hasChanges || hasErrors || submitting}
      >
        {submitting ? "Salvando..." : "Salvar Alterações"}
      </SaveButton>
    </Form>
  );
}
